import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { isHttpError, useHttp } from '@mono/http-hook';
import useSession from '../../session/useSession';

export const useCurrentOrganization = () => {
  const session = useSession();
  const params = useParams<{ orgSlug: string }>();

  return (
    session.data?.organizations.find((org) => org.slug === params.orgSlug) ??
    null
  );
};

export type OrganizationDetails = {
  id: string;
  name: string;
  slug: string;
  createdAt: string;
  updatedAt: string;
};

type OrganizationResponse = {
  organization: OrganizationDetails;
};

const getHttpErrorMessage = (error: unknown, fallback: string) => {
  if (isHttpError(error)) {
    const responseData = error.response?.data;
    if (responseData && typeof responseData === 'object') {
      const { error: errorMessage, message: responseMessage } =
        responseData as {
          error?: string;
          message?: string;
        };
      return errorMessage ?? responseMessage ?? error.message ?? fallback;
    }
    return error.message ?? fallback;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return fallback;
};

export const useOrganizationSettings = () => {
  const http = useHttp();
  const session = useSession();
  const currentOrganization = useCurrentOrganization();
  const organizationId = currentOrganization?.id ?? '';

  const [organization, setOrganization] = useState<OrganizationDetails | null>(
    null
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const fetchOrganization = useCallback(async () => {
    if (!organizationId) {
      setOrganization(null);
      setIsLoading(false);
      setSaveError(null);
      setLoadError(null);
      return;
    }

    setIsLoading(true);
    setLoadError(null);

    try {
      const { data } = await http.get<OrganizationResponse>(
        `/api/v1/organizations/${organizationId}`,
        { withCredentials: true }
      );

      setOrganization(data.organization);
      setSaveError(null);
    } catch (error) {
      setOrganization(null);
      setSaveError(null);
      setLoadError(
        getHttpErrorMessage(
          error,
          'Unable to load organization details right now.'
        )
      );
    } finally {
      setIsLoading(false);
    }
  }, [http, organizationId]);

  useEffect(() => {
    void fetchOrganization();
  }, [fetchOrganization]);

  const updateOrganization = useCallback(
    async (input: { name: string }) => {
      if (!organizationId) {
        throw new Error('Organization is not selected.');
      }

      setSaveError(null);
      setIsSaving(true);

      try {
        const { data } = await http.patch<OrganizationResponse>(
          `/api/v1/organizations/${organizationId}`,
          input,
          { withCredentials: true }
        );

        setOrganization(data.organization);
        await session.update?.();
        return data.organization;
      } catch (error) {
        const message = getHttpErrorMessage(
          error,
          'Unable to update organization.'
        );
        setSaveError(message);
        throw new Error(message);
      } finally {
        setIsSaving(false);
      }
    },
    [http, organizationId, session]
  );

  return {
    organization,
    isLoading,
    loadError,
    refresh: fetchOrganization,
    updateOrganization,
    isSaving,
    saveError,
  };
};
